import {ActionIcon, Slider} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {Text} from 'src/design/atoms/Text';
import {Tooltip} from 'src/design/atoms/Tooltip';
import {IconMaximize} from '@tabler/icons-react';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {
  BADGE_CARD_HEIGHT_PX,
  BADGE_CARD_WIDTH_PX,
  computeFitScale,
  countBadgeSections,
  MAX_SCALE,
  MIN_SCALE,
  resolveHeightBudget,
  VIEWPORT_PADDING_PX,
} from './badgeTemplatePreviewFit';

export interface BadgeTemplatePreviewFrameProps {
  /** Merged (or raw) HTML to render. Rendered in a fully sandboxed iframe. */
  html: string | undefined;
  isLoading?: boolean;
  title?: string;
  emptyMessage?: string;
}

// Sandboxed, scrollable, scale-adjustable preview of badge template HTML.
// `sandbox=""` (no tokens granted) disables scripts, forms, top-level
// navigation, and same-origin access — a broken/hostile template cannot touch
// the app session. That also means the parent cannot read `contentDocument`
// to measure rendered content, so badge-section count is estimated by
// regex-scanning the raw `html` string instead. Badge HTML typically contains
// multiple `.badge` sections (front/back); default zoom fits both axes so the
// whole template is visible without scrolling.
export const BadgeTemplatePreviewFrame: React.FC<
  BadgeTemplatePreviewFrameProps
> = ({html, isLoading = false, title = 'Badge template preview', emptyMessage = 'Nothing to preview yet.'}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [windowHeight, setWindowHeight] = useState(
    typeof window !== 'undefined' ? window.innerHeight : 0
  );
  const [scaleOverride, setScaleOverride] = useState<number | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    // containerWidth is always a content-box width: clientWidth includes padding,
    // ResizeObserver's contentRect already excludes it.
    setContainerWidth(el.clientWidth - VIEWPORT_PADDING_PX);

    const observer = new ResizeObserver(entries => {
      const entry = entries[0];
      if (entry) setContainerWidth(entry.contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const onResize = () => setWindowHeight(window.innerHeight);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const heightBudget = resolveHeightBudget(windowHeight);
  const badgeCount = useMemo(() => countBadgeSections(html), [html]);
  const contentHeight = BADGE_CARD_HEIGHT_PX * badgeCount;

  const fitScale = useMemo(
    () => computeFitScale({containerWidth, heightBudget, contentHeight}),
    [containerWidth, heightBudget, contentHeight]
  );

  const scale = scaleOverride ?? fitScale;
  const isOverridden = scaleOverride !== null;

  const scaledWidth = (BADGE_CARD_WIDTH_PX * scale) / 100;
  const scaledContentHeight = (contentHeight * scale) / 100;
  const viewportHeight = Math.min(
    scaledContentHeight + VIEWPORT_PADDING_PX,
    heightBudget
  );

  return (
    <Box data-testid="badge-template-preview-frame">
      <Group justify="space-between" mb="xs">
        <Text size="sm" fw={600}>
          Live Preview
        </Text>
        <Group gap="xs" wrap="nowrap">
          <Text size="xs" c="dimmed">
            Zoom
          </Text>
          <Slider
            w={140}
            min={MIN_SCALE}
            max={MAX_SCALE}
            step={5}
            value={scale}
            onChange={setScaleOverride}
            label={value => `${value}%`}
            aria-label="Preview zoom"
          />
          <Tooltip label="Fit to preview area">
            <ActionIcon
              variant="subtle"
              size="sm"
              disabled={!isOverridden}
              onClick={() => setScaleOverride(null)}
              aria-label="Fit preview to viewport">
              <IconMaximize size={14} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>

      <Box
        ref={containerRef}
        style={{
          border: '1px solid var(--mantine-color-gray-3)',
          borderRadius: 4,
          background: 'var(--mantine-color-gray-1)',
          width: '100%',
          height: viewportHeight,
          overflow: 'auto',
          padding: 12,
          display: 'flex',
          justifyContent: 'center',
        }}>
        {isLoading ? (
          <Group justify="center" h="100%" style={{width: '100%'}}>
            <Loader size="sm" />
          </Group>
        ) : html ? (
          <div style={{width: scaledWidth, height: scaledContentHeight, flexShrink: 0}}>
            <iframe
              title={title}
              data-testid="badge-template-preview-iframe"
              srcDoc={html}
              sandbox=""
              style={{
                width: BADGE_CARD_WIDTH_PX,
                height: contentHeight,
                border: 'none',
                background: 'white',
                transform: `scale(${scale / 100})`,
                transformOrigin: 'top left',
              }}
            />
          </div>
        ) : (
          <Text size="sm" c="dimmed" ta="center" py="xl">
            {emptyMessage}
          </Text>
        )}
      </Box>
    </Box>
  );
};
